/**
 * CommandPalette – global Ctrl+K / Cmd+K search overlay.
 * Searches pages (derived from the useNavGroups nav model), tickers and
 * terminal commands from anywhere in the app.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, X, PieChart, TrendingUp, Target, Shield, Wallet,
  Terminal, Zap, Clock, ArrowRight, UserCog, Sparkles, Sun,
  CandlestickChart, KeyRound,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavGroups } from '@/hooks/useNavGroups'

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */
/*  The searchable page list is DERIVED from useNavGroups — the single */
/*  nav model shared with SideNav + TopNav. It is already capability-  */
/*  filtered, and merged/deleted pages vanish automatically. Only      */
/*  non-page entries (quick actions, tickers, terminal commands,       */
/*  utility pages with no nav entry) are defined locally.              */

interface QuickAction {
  label: string
  description: string
  to: string
  icon: LucideIcon
}

const QUICK_ACTIONS: QuickAction[] = [
  { label: 'Today',              description: 'What should I do today?',        to: '/app',                 icon: Sun },
  { label: 'Invest Cash',        description: 'Where should new money go?',     to: '/app/deploy-cash',           icon: Sparkles },
  { label: 'FIRE Status',        description: 'Are we retirement ready?',       to: '/app/planning',              icon: Target },
  { label: 'Net Worth',          description: 'Full household breakdown',       to: '/app/net-worth',             icon: Wallet },
  { label: 'Portfolio',          description: 'Holdings & performance',         to: '/app/portfolio',             icon: PieChart },
  { label: 'Check Risk',         description: 'Portfolio risk dashboard',       to: '/app/risk',                  icon: Shield },
  { label: 'View Markets',       description: 'Market indices & data',          to: '/app/indices',               icon: TrendingUp },
  { label: 'Run Agent Analysis', description: 'Launch AI analysis workflow',    to: '/app/terminal?cmd=/analyze', icon: Zap },
  { label: 'Open Terminal',      description: 'Execute commands',               to: '/app/terminal',              icon: Terminal },
  { label: 'Household Profile',  description: 'Edit ages, incomes, targets',    to: '/app/profile',               icon: UserCog },
]

/* Utility pages that deliberately have no nav entry but should stay
   reachable from the palette. */
interface ExtraPage {
  to: string
  label: string
  icon: LucideIcon
  tooltip: string
  keywords?: string[]
}

const EXTRA_PAGES: ExtraPage[] = [
  { to: '/app/change-password', label: 'Change Password', icon: KeyRound,
    tooltip: 'Need to change your password?', keywords: ['security', 'reset'] },
]

/* Common tickers for quick match */
const POPULAR_TICKERS = [
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA', 'JPM', 'V', 'MA',
  'UNH', 'XOM', 'JNJ', 'WMT', 'PG', 'HD', 'KO', 'PEP', 'MRK', 'ABBV',
  'AVGO', 'COST', 'TMO', 'MCD', 'CSCO', 'ACN', 'ABT', 'DHR', 'LIN', 'NEE',
  'NFLX', 'AMD', 'INTC', 'DIS', 'BA', 'GS', 'MS', 'BLK', 'SCHW', 'C',
]

/* ------------------------------------------------------------------ */
/*  localStorage for recent searches                                   */
/* ------------------------------------------------------------------ */

const RECENT_KEY = 'command-palette-recent'
const MAX_RECENT = 10

function getRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function addRecent(query: string) {
  const q = query.trim()
  if (!q) return
  const list = getRecent().filter(r => r !== q)
  list.unshift(q)
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)))
}

/* ------------------------------------------------------------------ */
/*  Search result types                                                */
/* ------------------------------------------------------------------ */

interface SearchResult {
  id: string
  category: 'page' | 'ticker' | 'command' | 'quick'
  label: string
  description?: string
  icon: LucideIcon
  action: () => void
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Searchable pages = the single nav model (capability-filtered) plus a
  // few no-nav utility pages.
  const navGroups = useNavGroups()
  const pages = useMemo(
    () => [
      ...navGroups.flatMap(g => g.items.map(i => ({
        to: i.to, label: i.label, icon: i.icon,
        tooltip: i.tooltip, keywords: i.keywords,
      }))),
      ...EXTRA_PAGES,
    ],
    [navGroups],
  )

  // Load recent searches when opened
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
      setRecentSearches(getRecent())
      // Focus input after render
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Build results
  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase()

    if (!q) {
      // Show quick actions when empty
      return QUICK_ACTIONS.map((a, i) => ({
        id: `quick-${i}`,
        category: 'quick' as const,
        label: a.label,
        description: a.description,
        icon: a.icon,
        action: () => { navigate(a.to); onClose() },
      }))
    }

    const out: SearchResult[] = []

    // Pages — label + plain-English tooltip + keywords are all searchable
    pages.forEach(p => {
      const haystack = [p.label, p.tooltip, ...(p.keywords ?? [])].join(' ').toLowerCase()
      if (haystack.includes(q)) {
        out.push({
          id: `page-${p.to}`,
          category: 'page',
          label: p.label,
          description: p.tooltip,
          icon: p.icon,
          action: () => { addRecent(query); navigate(p.to); onClose() },
        })
      }
    })

    // Tickers – uppercase match
    const upper = query.trim().toUpperCase()
    if (/^[A-Z]{1,5}$/.test(upper)) {
      const matched = POPULAR_TICKERS.filter(t => t.startsWith(upper)).slice(0, 5)
      matched.forEach(t => {
        out.push({
          id: `ticker-${t}`,
          category: 'ticker',
          label: t,
          description: `View chart for ${t}`,
          icon: CandlestickChart,
          action: () => { addRecent(query); navigate(`/app/chart?symbol=${t}`); onClose() },
        })
      })
      // If exact match not found but looks like a ticker, still offer it
      if (matched.length === 0 && upper.length >= 1) {
        out.push({
          id: `ticker-${upper}`,
          category: 'ticker',
          label: upper,
          description: `Search chart for ${upper}`,
          icon: CandlestickChart,
          action: () => { addRecent(query); navigate(`/app/chart?symbol=${upper}`); onClose() },
        })
      }
    }

    // Commands – if starts with /
    if (q.startsWith('/')) {
      out.push({
        id: `cmd-${q}`,
        category: 'command',
        label: query.trim(),
        description: 'Run in Terminal',
        icon: Terminal,
        action: () => { addRecent(query); navigate(`/app/terminal?cmd=${encodeURIComponent(query.trim())}`); onClose() },
      })
    }

    // (Alt-data keyword special-case removed — the Alt Data nav entry's
    //  keywords cover congress/insider/lobbying/patent/sec/filing.)

    return out
  }, [query, pages, navigate, onClose])

  // Clamp active index
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[activeIndex]) {
      e.preventDefault()
      results[activeIndex].action()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }, [results, activeIndex, onClose])

  if (!open) return null

  const categoryLabel: Record<string, string> = {
    page: 'Pages',
    ticker: 'Tickers',
    command: 'Commands',
    quick: 'Quick Actions',
  }

  // Group results by category
  const grouped: { category: string; items: SearchResult[] }[] = []
  const seen = new Set<string>()
  results.forEach(r => {
    if (!seen.has(r.category)) {
      seen.add(r.category)
      grouped.push({ category: r.category, items: [] })
    }
    grouped.find(g => g.category === r.category)!.items.push(r)
  })

  // Flat index mapping
  let flatIdx = 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={onClose}
      aria-label="Command palette"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg mx-4 glass rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.35),0_32px_80px_rgba(0,0,0,0.45)] overflow-hidden animate-slide-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search size={18} className="text-text-muted shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, tickers, commands..."
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none"
            aria-label="Search"
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-0.5 rounded hover:bg-surface-card transition-colors"
              aria-label="Clear search"
            >
              <X size={14} className="text-text-muted" />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono text-text-muted bg-surface-card border border-border">
            ESC
          </kbd>
        </div>

        {/* Recent searches */}
        {!query && recentSearches.length > 0 && (
          <div className="px-3 pt-3 pb-1">
            <p className="panel-title px-2 pb-1.5">
              Recent Searches
            </p>
            <div className="flex flex-wrap gap-1.5">
              {recentSearches.map((r, i) => (
                <button
                  key={i}
                  onClick={() => setQuery(r)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs text-text-secondary bg-surface-card hover:bg-blue/10 hover:text-blue-light transition-colors"
                >
                  <Clock size={10} aria-hidden />
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto px-2 py-2" role="listbox">
          {grouped.length === 0 && query && (
            <div className="px-4 py-8 text-center text-sm text-text-muted">
              No results for "{query}"
            </div>
          )}
          {grouped.map(group => (
            <div key={group.category}>
              <p className="panel-title px-3 pt-2 pb-1">
                {categoryLabel[group.category] ?? group.category}
              </p>
              {group.items.map(item => {
                const idx = flatIdx++
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    data-index={idx}
                    role="option"
                    aria-selected={idx === activeIndex}
                    onClick={item.action}
                    className={clsx(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                      idx === activeIndex
                        ? 'bg-blue/10 text-blue-light'
                        : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                    )}
                  >
                    <Icon size={16} aria-hidden className="shrink-0" />
                    <div className="flex-1 text-left min-w-0">
                      <span className="font-medium">{item.label}</span>
                      {item.description && (
                        <span className="ml-2 text-xs text-text-muted">{item.description}</span>
                      )}
                    </div>
                    {idx === activeIndex && (
                      <ArrowRight size={14} className="shrink-0 text-blue-light" aria-hidden />
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-border text-[10px] text-text-muted">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-surface-card border border-border font-mono">↑</kbd>
              <kbd className="px-1 py-0.5 rounded bg-surface-card border border-border font-mono">↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-surface-card border border-border font-mono">↵</kbd>
              select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-surface-card border border-border font-mono">Ctrl</kbd>
            +
            <kbd className="px-1 py-0.5 rounded bg-surface-card border border-border font-mono">K</kbd>
            to search
          </span>
        </div>
      </div>
    </div>
  )
}
